import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

// Styles
import "./styles/App.scss";

// Assets
// => Venue Branding
import venueLogo from "./assets/venueLogo.svg";
import AxiLogoPrimary from "./static/AxiLogoPrimary.png";

// Components
import Header from "./components/Header";
import Chat from "./components/Chat";
import ChatCompleteNotification from "./components/ChatCompleteNotification";
import Footer from "./components/Footer";
import ThemeProvider from "./components/ThemeProvider";

// Constants
import { APP_CONFIG } from "./constants";
import { EMBED_CONFIG } from "./utils/embedConfig";
import { postReady, postClose, onConfig, onTeardown } from "./utils/embedMessaging";

const { APP_ID: appId, SHOW_LOGO: showLogo, BACKEND_URL: backendUrl } = APP_CONFIG;

let pathnameRaw = window.location.pathname;
if (pathnameRaw.endsWith("/")) {
  pathnameRaw = pathnameRaw.slice(0, -1);
}
const pathname = pathnameRaw.split("/");
const place = pathname[pathname.length - 1];

function App() {
  const [systemStatus, setSystemStatus] = useState("initial");
  const [messages, setMessages] = useState([]);
  const [lastMessage, setLastMessage] = useState({});
  const [venueName, setVenueName] = useState("");
  const [merchantURL, setMerchantURL] = useState("");
  const [servicePlace, setServicePlace] = useState("");
  const [bannerImage, setBannerImage] = useState("");
  const [integrationType, setIntegrationType] = useState("");
  const [audioStream, setAudioStream] = useState("");
  const [langName, setLangName] = useState("");
  const [venueLangCodeList, setVenueLangCodeList] = useState("");
  const [displaySummaryWindow, setDisplaySummaryWindow] = useState(false);
  const [error, setError] = useState(null);

  // Embed-mode preset mode — host can update via sendConfig({ mode: ... })
  // to pre-select Voice/Text before the user reaches the mode selector.
  const [embedPresetMode, setEmbedPresetMode] = useState(EMBED_CONFIG.presetMode);

  // Ref to the live ChatMessages instance so we can tear down the voice
  // session / release the mic when the widget is closed.
  const chatRef = useRef(null);

  const requestMicrophoneAccess = useCallback(async () => {
    const existingStream =
      audioStream &&
      typeof audioStream.getTracks === "function" &&
      audioStream.getTracks().some((track) => track.readyState === "live")
        ? audioStream
        : null;

    if (existingStream) {
      return existingStream;
    }

    if (
      typeof navigator === "undefined" ||
      !navigator.mediaDevices?.getUserMedia
    ) {
      throw new Error("Microphone access is not supported in this browser.");
    }

    const supportedConstraints = navigator.mediaDevices.getSupportedConstraints
      ? navigator.mediaDevices.getSupportedConstraints()
      : {};
    const preferredAudioConstraints = {};

    if (supportedConstraints.echoCancellation) {
      preferredAudioConstraints.echoCancellation = true;
    }
    if (supportedConstraints.noiseSuppression) {
      preferredAudioConstraints.noiseSuppression = true;
    }
    if (supportedConstraints.autoGainControl) {
      preferredAudioConstraints.autoGainControl = false;
    }
    if (supportedConstraints.channelCount) {
      preferredAudioConstraints.channelCount = 1;
    }

    const hasPreferredConstraints =
      Object.keys(preferredAudioConstraints).length > 0;
    const requestConstraints = hasPreferredConstraints
      ? { audio: preferredAudioConstraints }
      : { audio: true };

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia(requestConstraints);
    } catch (error) {
      if (
        hasPreferredConstraints &&
        (error.name === "OverconstrainedError" ||
          error.name === "NotReadableError")
      ) {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } else {
        console.error("Microphone access failed:", error);
        throw error;
      }
    }

    const [track] = stream.getAudioTracks();
    if (
      track &&
      hasPreferredConstraints &&
      typeof track.applyConstraints === "function"
    ) {
      track
        .applyConstraints(preferredAudioConstraints)
        .catch((constraintError) => {
          console.warn(
            "Microphone constraint application failed:",
            constraintError
          );
        });
    }

    setAudioStream(stream);
    return stream;
  }, [audioStream]);

  const getServices = async (place) => {
    try {
      const response = await axios.get(backendUrl + "venues/" + place);
      setServicePlace(response.data.Place);
      setVenueName(response.data.OfficialName);
      setBannerImage(response.data.BannerBase64);
      setMerchantURL(response.data.MerchantURL);
      setIntegrationType(response.data.IntegrationType);
      setAudioStream(navigator.mediaDevices.getUserMedia({ audio: true }));
      setLangName(response.data.LanguageCode);
      setVenueLangCodeList(response.data.VenueLangCodeList);
    } catch (err) {
      console.error("Error loading service details:", err);
      if (err.response && err.response.status === 404) {
        setError(404);
      } else {
        setError(500);
      }
    }
  };

  useEffect(() => {
    getServices(place);
  }, []);

  // Embed bridge: announce ready on mount, then accept mode pre-selection
  useEffect(() => {
    if (!EMBED_CONFIG.embed) return undefined;
    postReady();
    const unsubscribeConfig = onConfig((data) => {
      if (data.mode === "voice" || data.mode === "text") setEmbedPresetMode(data.mode);
    });
    const unsubscribeTeardown = onTeardown(() => {
      if (chatRef.current && typeof chatRef.current.teardownSession === "function") {
        chatRef.current.teardownSession();
      }
    });
    return () => {
      unsubscribeConfig();
      unsubscribeTeardown();
    };
  }, []);
  
  useEffect(() => {
    if (!EMBED_CONFIG.embed) return undefined;
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById("root");
    const prev = {
      htmlHeight: html.style.height,
      htmlOverflow: html.style.overflow,
      bodyHeight: body.style.height,
      bodyOverflow: body.style.overflow,
      bodyMargin: body.style.margin,
      rootHeight: root ? root.style.height : "",
    };
    html.style.height = "100%";
    html.style.width = "100%";
    html.style.overflow = "hidden";
    body.style.height = "100%";
    body.style.width = "100%";
    body.style.overflow = "hidden";
    body.style.margin = "0";
    if (root) {
      root.style.height = "100%";
      root.style.width = "100%";
      root.style.overflow = "hidden";
    }
    return () => {
      html.style.height = prev.htmlHeight;
      html.style.overflow = prev.htmlOverflow;
      body.style.height = prev.bodyHeight;
      body.style.overflow = prev.bodyOverflow;
      body.style.margin = prev.bodyMargin;
      if (root) root.style.height = prev.rootHeight;
    };
  }, []);

  const handleEmbedClose = useCallback(() => {
    if (chatRef.current && typeof chatRef.current.teardownSession === "function") {
      chatRef.current.teardownSession();
    }
    postClose();
  }, []);

  return error ? (
    <div className="axify__widget-error-container">
      <div className="axify__widget-error-content">
        <img
          src={AxiLogoPrimary}
          alt="Axi Logo"
          className="axify__widget-error-logo"
        />
        <h1 className="axify__widget-error-title">
          Oops! Something went wrong
        </h1>
        <p className="axify__widget-error-message">
          {error === 404
            ? "We couldn't find the location you're looking for. Please check the URL or contact your venue."
            : "We're having trouble loading the service details. Please try refreshing the page or contact support if the issue persists."}
        </p>
      </div>
    </div>
  ) : (
    <ThemeProvider
      id={"axify__widget-" + appId}
      className="axify__widget"
      data-state="open"
      data-axify-embed={EMBED_CONFIG.embed ? "true" : undefined}
      aria-expanded="true"
      aria-hidden="false"
      themeName={EMBED_CONFIG.themeName || undefined}
      forceLayout={EMBED_CONFIG.embed ? "compact" : undefined}
    >
      <div className="axify__widget-inner">
        <Header
          appId={appId}
          venueName={venueName}
          venueLogo={venueLogo}
          bannerImage={bannerImage}
          showLogo={showLogo}
          merchantURL={merchantURL}
          agentDisplayName={EMBED_CONFIG.agentDisplayName}
          onClose={EMBED_CONFIG.embed ? handleEmbedClose : undefined}
        />

        <Chat
          appId={appId}
          systemStatus={systemStatus}
          setSystemStatus={setSystemStatus}
          messages={messages}
          setMessages={setMessages}
          setLastMessage={setLastMessage}
          servicePlace={servicePlace}
          merchantURL={merchantURL}
          integrationType={integrationType}
          audioStream={audioStream}
          displaySummaryWindow={displaySummaryWindow}
          setDisplaySummaryWindow={setDisplaySummaryWindow}
          requestMicrophoneAccess={requestMicrophoneAccess}
          langName={langName}
          venueLangCodeList={venueLangCodeList}
          presetMode={embedPresetMode}
          chatRef={chatRef}
        />

        <Footer
          appId={appId}
          display={displaySummaryWindow}
          merchantURL={merchantURL}
        />

        <ChatCompleteNotification
          appId={appId}
          systemStatus={systemStatus}
          message={lastMessage}
        />
      </div>
    </ThemeProvider>
  ); // return
}

export default App;
